import { useRef, type ComponentProps, type ReactNode } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { VoiceInputButton } from '@/components/common/chat/VoiceInputButton';
import { useVoiceInput } from '@/components/common/chat/useVoiceInput';

export interface TextInputProps extends Omit<
  ComponentProps<typeof Textarea>,
  'value' | 'onChange'
> {
  value: string;
  onValueChange: (value: string) => void;
  /**
   * Hold-to-speak dictation into the field. On by default — pass false where
   * a mic doesn't fit (e.g. fields holding machine-parsed formats).
   */
  enableVoiceInput?: boolean;
  /**
   * Composer-style submit action rendered inside the field's container, so
   * it is unambiguous that the action consumes this field's text. Dictation
   * never triggers it — the user always confirms by clicking — and it is
   * held disabled while a transcription is still streaming so a click can't
   * submit partial text. Provide `actionLabel` together with `onAction`.
   */
  actionLabel?: ReactNode;
  onAction?: () => void;
  /** Disable only the action button (e.g. nothing to submit yet). */
  actionDisabled?: boolean;
}

/**
 * One dictation session = one mic press-and-release. The transcript streams
 * in cumulatively (each delta is the whole utterance so far), so the field is
 * re-derived each time from the text present at mic-press (`base`) plus the
 * current transcript. `written` records every value the session has pushed:
 * if a render ever shows a value the session didn't produce, the user typed
 * or a submit cleared/reset the field, and the session is cancelled so
 * late-arriving transcription can't clobber that change.
 */
interface DictationSession {
  base: string;
  written: Set<string>;
  cancelled: boolean;
}

/**
 * Base free-text input: the shadcn Textarea plus the optional capabilities
 * every free-text field can share. Today that's voice dictation — the same
 * hold-to-speak mic as the chat composer — and an attached submit action;
 * more can hang here later. Disable a capability per call site via options
 * when it doesn't fit.
 *
 * Unlike the chat composer, dictation appends to whatever is already in the
 * field and simply stays there; nothing is submitted without an explicit
 * click on the action.
 */
export function TextInput({
  value,
  onValueChange,
  enableVoiceInput = true,
  actionLabel,
  onAction,
  actionDisabled = false,
  className,
  disabled,
  ...textareaProps
}: TextInputProps) {
  const valueRef = useRef(value);
  valueRef.current = value;
  const sessionRef = useRef<DictationSession | null>(null);

  // Cancel the active dictation session when the field changes under it.
  // Transcription keeps streaming for a moment after mic release; without
  // this, a submit that clears the field (or the user typing) would be
  // overwritten by the late transcript.
  const session = sessionRef.current;
  if (
    session &&
    !session.cancelled &&
    value !== session.base &&
    !session.written.has(value)
  ) {
    session.cancelled = true;
  }

  const handleTranscript = (text: string | null) => {
    if (text === null) {
      // Mic released and the final transcript (if any) delivered — the
      // session is over. The text stays in the field.
      sessionRef.current = null;
      return;
    }
    // A session starts with an empty transcript at mic-press; snapshot the
    // field so the utterance appends to it.
    sessionRef.current ??= {
      base: valueRef.current,
      written: new Set(),
      cancelled: false,
    };
    const active = sessionRef.current;
    if (active.cancelled || !text) return;

    const base = active.base.replace(/\s+$/, '');
    const joined = base ? `${base} ${text}` : text;
    // Native maxLength only constrains typing, not programmatic sets.
    const { maxLength } = textareaProps;
    const next = maxLength ? joined.slice(0, maxLength) : joined;
    active.written.add(next);
    onValueChange(next);
  };

  const voice = useVoiceInput({
    onTranscript: handleTranscript,
    disabled: disabled || !enableVoiceInput,
  });

  if (onAction) {
    return (
      <div className="rounded-md border bg-background overflow-hidden">
        <Textarea
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          className={cn(
            'resize-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0',
            className,
          )}
          disabled={disabled}
          {...textareaProps}
        />
        <div className="flex items-center justify-between px-2 py-1.5 border-t bg-muted/50">
          <div className="flex items-center gap-0.5">
            {enableVoiceInput && (
              <VoiceInputButton controls={voice} disabled={disabled} />
            )}
          </div>
          <Button
            type="button"
            size="sm"
            className="h-7"
            onClick={onAction}
            disabled={
              disabled ||
              actionDisabled ||
              voice.recording ||
              voice.isTranscribing
            }
          >
            {actionLabel}
          </Button>
        </div>
      </div>
    );
  }

  if (!enableVoiceInput) {
    return (
      <Textarea
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        className={className}
        disabled={disabled}
        {...textareaProps}
      />
    );
  }

  return (
    <div className="relative">
      <Textarea
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        className={cn('pr-10', className)}
        disabled={disabled}
        {...textareaProps}
      />
      <VoiceInputButton
        controls={voice}
        disabled={disabled}
        className="absolute top-1 right-1"
      />
    </div>
  );
}
